import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { planTypeProviderSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { useSignUpStore } from "~/stores";
import { getProviderPlanTypes } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

// Provider-specific plan type options
export const PROVIDER_PLAN_TYPE_OPTIONS: Record<
  string,
  Record<string, string>
> = {
  fidelis: {
    medicaid: "Medicaid",
    medicare: "Medicare",
    CHP: "CHP",
  },
  trinityMedicare: {
    medicare: "Medicare",
  },
  harvardPilgrim: {
    medicare: "Medicare",
    employer: "Employer",
  },
  upmc: {
    medicare: "Medicare",
    employer: "Employer",
  },
  wellpoint: {
    medicaid: "Medicaid",
    CHP: "CHP",
    HARP: "HARP",
    essentialPlan: "Essential Plan",
  },
};

interface PlanTypeProviderFormProps {
  onPrev: () => void;
  onNext: (response?: string) => void;
  isSecondaryProvider?: boolean;
}

export const PlanTypeProviderForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: PlanTypeProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const currentInsurance = isSecondaryProvider
    ? secondaryInsurance
    : primaryInsurance;
  const providerName = currentInsurance?.providerName;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.planTypeProvider
    : primaryInsurance.planTypeProvider;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "plan_type_provider",
  });

  const planTypes = getProviderPlanTypes(providerName);

  const providerOverrides = providerName
    ? PROVIDER_PLAN_TYPE_OPTIONS[providerName]
    : undefined;

  const planTypeProviderOptions = planTypes.map((type) => ({
    value: type,
    label:
      providerOverrides?.[type] ?? type.charAt(0).toUpperCase() + type.slice(1),
  }));

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof planTypeProviderSchema>>({
    mode: "onBlur",
    resolver: zodResolver(planTypeProviderSchema),
    defaultValues: {
      planTypeProvider: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("planTypeProvider", defaultValue ?? "");
    setSelectedValue(defaultValue ?? "");
  }, [defaultValue, setValue]);

  useEffect(() => {
    const validValues = planTypeProviderOptions.map((o) => o.value);
    if (defaultValue && !validValues.includes(defaultValue)) {
      setValue("planTypeProvider", "");
      setSelectedValue("");
      setPatientInsuranceField(
        "planTypeProvider",
        undefined,
        isSecondaryProvider,
      );
    }
  }, [
    providerName,
    planTypeProviderOptions,
    defaultValue,
    setValue,
    setPatientInsuranceField,
    isSecondaryProvider,
  ]);

  const handleOptionChange = (value: string) => {
    setSelectedValue(value);
    setValue("planTypeProvider", value);
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof planTypeProviderSchema>> = (
    data,
  ) => {
    setPatientInsuranceField(
      "planTypeProvider",
      data.planTypeProvider,
      isSecondaryProvider,
    );
    trackComplete({ plan_type: data.planTypeProvider });

    onNext(data.planTypeProvider);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField(
      "planTypeProvider",
      undefined,
      isSecondaryProvider,
    );
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          Is your insurance plan through an employer, Medicaid, or Medicare?
        </h1>

        <RadioCardGroup
          options={planTypeProviderOptions}
          value={selectedValue}
          onChange={handleOptionChange}
        />

        {errors.planTypeProvider && (
          <ErrorMessage message={errors.planTypeProvider.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select an option to continue"
      />
    </form>
  );
};
